import React, { useEffect } from "react";
import { useI18n } from "../../utils/i18n/I18nProvider";
import { useGameStore, selectUser, selectIsInitialized } from "../../store/gameStore";
import { initializePerformance } from "../../utils/performance";
import { trackLoadingScreen } from "../../utils/analytics/trackingEvents";

interface AppWithLoadingStateProps {
  children: React.ReactNode;
}

/**
 * This wrapper component ensures the loading screen stays visible
 * until both the i18n resources and UserService are fully loaded, preventing the flash
 * of untranslated text (FOUT) and ensuring user data is available before rendering.
 * It also initializes the performance detection system early in the app lifecycle.
 */
export function AppWithLoadingState({ children }: AppWithLoadingStateProps) {
  const { isLoaded: isI18nLoaded } = useI18n();
  const user = useGameStore(selectUser);
  const isInitialized = useGameStore(selectIsInitialized);

  useEffect(() => {
    // Initialize performance detection early in app lifecycle
    initializePerformance().catch((error) => {
      console.error("Failed to initialize performance detection:", error);
    });
  }, []);

  useEffect(() => {
    if (isI18nLoaded && isInitialized && user) {
      console.log(
        "✅ i18n resources and store initialized, hiding loading screen"
      );

      // Hide loading screen after React has mounted and everything is ready
      // Exception: Don't hide for game page, let GameComponent handle it
      setTimeout(() => {
        const currentPath = window.location.pathname;
        if (
          currentPath !== "/game" &&
          typeof window.hideLoadingScreen === "function"
        ) {
          window.hideLoadingScreen();
          trackLoadingScreen.hidden();
        }
      }, 100);
    }
  }, [isI18nLoaded, isInitialized, user]);

  // Don't render children until both i18n and store are loaded
  if (!isI18nLoaded || !isInitialized || !user) {
    return null;
  }

  return <>{children}</>;
}
